import { Agent } from '@kanbu/schema/enums';
import { Trans } from '@lingui/react/macro';
import { SelectPrimitive as Select } from '@utima/ui';
import {
  FormControl,
  useFormContext,
  type FormControlProps,
} from '@utima/ui-informed';
import get from 'lodash.get';
import { BrainIcon, ShoppingCartIcon } from 'lucide-react';

import { IconWrapper } from '@/components/iconWrapper/IconWrapper';

export interface AgentSelectorProps
  extends Omit<FormControlProps, 'render' | 'type' | 'children'> {}

export function AgentSelector({ name, ...restProps }: AgentSelectorProps) {
  const { initialValues } = useFormContext<unknown>();
  const nameWithoutId = name.endsWith('.id') ? name.slice(0, -3) : name;
  const autoInitialData = get(initialValues, nameWithoutId) as
    | string
    | undefined;
  const resolvedDefault = autoInitialData ?? Agent.Conversational;

  return (
    <FormControl
      {...restProps}
      name={name}
      type='text'
      defaultValue={resolvedDefault}
      render={({ ref, userProps, fieldApi, fieldState }) => {
        const value =
          (fieldState.value as string | undefined) ?? resolvedDefault;

        return (
          <Select.Root
            variant={fieldState.showError ? 'danger' : 'default'}
            value={value}
            onValueChange={nextValue => {
              fieldApi.setTouched(true);
              fieldApi.setValue(nextValue || null);
            }}
          >
            <div className='[&>button]:!h-auto [&>button]:!p-1'>
              <Select.Trigger id={userProps.id}>
                <Select.Value />
              </Select.Trigger>
            </div>
            <Select.Content
              ref={ref}
              className='max-w-sm [&_*[role=option]>span]:end-2 [&_*[role=option]>span]:start-auto [&_*[role=option]]:pe-8 [&_*[role=option]]:ps-2'
            >
              <Select.Item value={Agent.Conversational} className='h-auto'>
                <span className='flex items-start gap-2 p-1'>
                  <IconWrapper
                    Icon={BrainIcon}
                    className='bg-emerald-500 text-white'
                  />
                  <div>
                    <span className='block font-bold'>
                      <Trans>AI Assistant</Trans>
                    </span>
                    <span className='mt-0.5 block text-xs'>
                      <Trans>
                        Suitable for customer communication,{' '}
                        <strong>
                          can answer questions based on its own knowledge base
                        </strong>
                        or transfer to operators.
                      </Trans>
                    </span>
                  </div>
                </span>
              </Select.Item>
              <Select.Item value={Agent.ShopAssistant} className='mt-1 h-auto'>
                <span className='flex items-start gap-2 p-1'>
                  <IconWrapper
                    Icon={ShoppingCartIcon}
                    className='bg-blue-500 text-white'
                  />
                  <div>
                    <span className='block font-bold'>
                      <Trans>E-commerce</Trans>
                    </span>
                    <span className='mt-0.5 block text-xs'>
                      <Trans>
                        Suitable for e-commerce.{' '}
                        <strong>Connects to your XML feeds</strong> and allows
                        you to search, filter and compare products.
                      </Trans>
                    </span>
                  </div>
                </span>
              </Select.Item>
            </Select.Content>
          </Select.Root>
        );
      }}
    />
  );
}
